import React, { useMemo, useState } from "react";
import { employees } from "../data/sampleData";
import { calculateFatigue } from "../../utils/calculateFatigue";
import "./GlobalFatigueView.css";

/* ============================
   DATE UTILS
============================ */
function getMonthDays(year, month) {
  const days = [];
  const d = new Date(year, month, 1);
  while (d.getMonth() === month) {
    days.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

/* ============================
   COMPONENT
============================ */
export default function GlobalFatigueView({ selectedSiteId }) {
  const today = new Date();

  const [view, setView] = useState(selectedSiteId ? "SITE" : "GLOBAL");
  const [activeCard, setActiveCard] = useState("");
  const [showAlerts, setShowAlerts] = useState(false);

  const [cursor, setCursor] = useState({
    year: today.getFullYear(),
    month: today.getMonth()
  });

  const days = useMemo(() => getMonthDays(cursor.year, cursor.month), [cursor]);

  const monthLabel = new Date(cursor.year, cursor.month).toLocaleString(
    "default",
    { month: "long", year: "numeric" }
  );

  /* ============================
     EMPLOYEE SCOPE
  ============================ */
  const scopedEmployees = useMemo(() => {
    console.log("view =", view, selectedSiteId);
    if (view === "SITE" && selectedSiteId) {
        console.log("view limited employees", employees );
      return employees.filter(e => e.homeSiteId === selectedSiteId);
    }
    console.log("view all employees =" );
    return employees;
  }, [view, selectedSiteId]);

  /* ============================
     EMPLOYEE-LEVEL STATS
  ============================ */
  const employeeStats = useMemo(() => {
    return scopedEmployees.map(emp => {
      let presentDays = 0;
      let absentDays = 0;
      let fatigue = null;
      const alertDays = [];
      const alertReasons = [];
      let noDataFlag = false;

      days.forEach(date => {
        const risk = calculateFatigue(emp.id, date);

        if (!risk) {
          noDataFlag = true;
          return;
        }

        if (risk.level === "off") {
          absentDays++;
          return;
        }

        if (risk.level === "normal") presentDays++;

        if (risk.level === "high") {
          fatigue = "HIGH";
          alertDays.push(date);
          risk.workedShifts.forEach(shift =>
            alertReasons.push({
              date,
              shift,
              reason: risk.tooltip,
              icon: risk.icon,
            })
          );
        } else if (risk.level === "medium" && fatigue !== "HIGH") {
          fatigue = "MEDIUM";
          alertDays.push(date);
          risk.workedShifts.forEach(shift =>
            alertReasons.push({
              date,
              shift,
              reason: risk.tooltip,
              icon: risk.icon,
            })
          );
        } else if (!fatigue && risk.level === "normal") {
          fatigue = "OK";
        }
      });

      return {
        emp,
        presentDays,
        absentDays,
        fatigue,
        alert: fatigue === "HIGH" || fatigue === "MEDIUM",
        alertDays,
        alertReasons,
        noData: noDataFlag
      };
    });
  }, [scopedEmployees, days]);

  /* ============================
     SUMMARY
  ============================ */
  const summary = useMemo(() => {
    let high = 0,
      medium = 0,
      ok = 0,
      present = 0,
      absent = 0,
      nodata = 0;

    employeeStats.forEach(e => {
      if (e.presentDays > 0) present++;
      if (e.absentDays > 0) absent++;
      if (e.noData) nodata++;

      if (e.fatigue === "HIGH") high++;
      if (e.fatigue === "MEDIUM") medium++;
      if (e.fatigue === "OK") ok++;
    });

    return {
      high,
      medium,
      ok,
      present,
      absent,
      nodata,
      total: employeeStats.length,
      alerts: high + medium
    };
  }, [employeeStats]);

  /* ============================
     FILTER HELPERS
  ============================ */
  function toggleCard(key) {
    setActiveCard(c => (c === key ? "" : key));
  }

  function allowCell(stat, date, risk) {
    if (!activeCard) return true;

    if (activeCard === "PRESENT") return risk && risk.level === "normal";
    if (activeCard === "ABSENT") return risk && risk.level === "off";
    if (activeCard === "NODATA") return !risk;
    return stat.alertDays.includes(date);
  }

  /* ============================
     UI
  ============================ */
  return (
    <div className="fatigue-page">
      {/* TOP BAR */}
      <div className="top-bar">
        <div className="tabs">
          {selectedSiteId && (
            <button
              className={view === "SITE" ? "active" : ""}
              onClick={() => setView("SITE")}
            >
              Site View
            </button>
          )}
          <button
            className={view === "GLOBAL" ? "active" : ""}
            onClick={() => setView("GLOBAL")}
            >
              Global View
          </button>
        </div>

        <button
          className="alerts-btn"
          onClick={() => setShowAlerts(s => !s)}
        >
          Alerts
          <span className="alert-badge">{summary.alerts}</span>
        </button>
      </div>

      {/* ALERT PANEL */}
      {showAlerts && summary.alerts > 0 && (
        <div className="alerts-panel">
          {employeeStats
            .filter(e => e.alert)
            .map(e => (
              <div key={e.emp.id} className="alert-card">
                <strong>{e.emp.name}</strong> ({e.fatigue})
                <div className="alert-details">
                  {e.alertReasons.map((r, i) => (
                    <div key={i} className="alert-detail">
                      <span className="alert-date">{r.date}</span>
                      <span className="alert-shift">{r.shift}</span>
                      <span className="alert-icon">{r.icon}</span>
                      <span className="alert-reason">{r.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* SUMMARY CARDS */}
      <div className="summary-cards">
        {["HIGH", "MEDIUM", "OK", "PRESENT", "ABSENT", "NODATA"].map(k => (
          <div
            key={k}
            className={`card ${k.toLowerCase()} ${
              activeCard === k ? "active" : ""
            }`}
            onClick={() => toggleCard(k)}
          >
            {k}
            <span>{summary[k.toLowerCase()]}</span>
          </div>
        ))}
        <div className="card total">
          TOTAL
          <span>{summary.total}</span>
        </div>
      </div>

      {/* MONTH BAR */}
      <div className="month-bar">
        <button
          onClick={() =>
            setCursor(c => ({
              year: c.month === 0 ? c.year - 1 : c.year,
              month: c.month === 0 ? 11 : c.month - 1
            }))
          }
        >
          ← Prev
        </button>

        <strong>{monthLabel}</strong>

        <button
          onClick={() =>
            setCursor(c => ({
              year: c.month === 11 ? c.year + 1 : c.month + 1,
              month: c.month === 11 ? 0 : c.month + 1
            }))
          }
        >
          Next →
        </button>
      </div>

      {/* MATRIX */}
      <div className="matrix-wrapper">
        <table className="fatigue-matrix">
          <thead>
            <tr>
              <th className="sticky">Employee</th>
              {days.map(d => (
                <th key={d}>{d.slice(8, 10)}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {employeeStats.map(stat => (
              <tr key={stat.emp.id}>
                <td className="sticky emp-cell">
                  <strong>{stat.emp.name}</strong>
                  <div className="meta">{stat.emp.designation}</div>
                </td>

                {days.map(date => {
                  const risk = calculateFatigue(stat.emp.id, date);

                  if (!risk) {
                    return (
                      <td key={date} className="cell nodata">
                        -
                      </td>
                    );
                  }

                  if (risk.level === "off") {
                    return (
                      <td key={date} className="cell absent">
                        ⚪
                      </td>
                    );
                  }

                  // NORMAL: show shift(s) and green background
                  const shiftText = risk.workedShifts.join(",") || "";
                  const bgClass =
                    risk.level === "high"
                      ? "bg-high"
                      : risk.level === "medium"
                      ? "bg-medium"
                      : "bg-normal";

                  const visible = allowCell(stat, date, risk);

                  return (
                    <td
                      key={date}
                      className={`cell ${bgClass} ${!visible ? "muted" : ""}`}
                      title={risk.tooltip}
                    >
                      {shiftText || risk.icon}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
